import React, { useRef } from 'react';

import useListToolbarPin from '../../hooks/useListToolbarPin';

/**
 * The filter row of a list of items, laid out per breakpoint: the collection
 * list's and a related list's (docs/platform-standards.md §5 Lists).
 *
 * - **Desktop** (`stacked` false): one wrapping row. The controls are the
 *   children, in the order the caller wants them.
 * - **Phone** (`stacked`): nothing scrolls sideways. `primary` is the first
 *   row (the card-type select and the view switch), each of `rows` a row of
 *   its own, `secondary` the filter tools, `commit` what the screen can be
 *   saved as (the saved filter's buttons, KPR-1642) on a row of its own right
 *   under them, `chips` the applied chips, which wrap on a row of their own.
 * - `search`: the list's search box, when it has one (KPR-740). It leads the
 *   desktop row and takes a row of its own under `primary` on a phone. Nothing
 *   is drawn when it is empty.
 *
 * - **Two rows** (`view` and `query`, the collection's items page, KPR-1633):
 *   on desktop the first row says what you are looking at and the second how
 *   it is narrowed and ordered. Each is `{ start, end }`: `start` reads from
 *   the left, `end` sits at the right edge; a row wraps within itself and an
 *   empty row is not drawn. A phone still takes the stacked rows above.
 *
 * - The two rows stick beneath the pinned shell header from 768px up
 *   (KPR-1724, `useListToolbarPin`); the stacked phone toolbar scrolls away.
 *
 * Empty rows are not drawn. The class names are the items page's, so the
 * stylesheet is shared.
 */
export default function ItemsListToolbar({
	stacked = false,
	search = null,
	primary = null,
	rows = [],
	secondary = null,
	commit = null,
	chips = null,
	view = null,
	query = null,
	children = null,
}) {
	if (!stacked && (view || query)) {
		return <TwoRowToolbar view={view} query={query} />;
	}
	if (!stacked) {
		return (
			<div className="items-page__toolbar">
				{search}
				{children}
			</div>
		);
	}
	return (
		<div className="items-page__toolbar items-page__toolbar--stacked">
			{primary ? (
				<div className="items-page__toolbar-row items-page__toolbar-row--primary">{primary}</div>
			) : null}
			{search ? <div className="items-page__toolbar-row items-page__toolbar-row--search">{search}</div> : null}
			{rows.filter(Boolean).map((row, index) => (
				<div key={index} className="items-page__toolbar-row">{row}</div>
			))}
			{secondary ? (
				<div className="items-page__toolbar-row items-page__toolbar-row--secondary">{secondary}</div>
			) : null}
			{hasContent(commit) ? (
				<div className="items-page__toolbar-row items-page__toolbar-row--commit" data-toolbar-commit>{commit}</div>
			) : null}
			{chips ? (
				<div className="items-page__toolbar-row items-page__toolbar-row--chips">{chips}</div>
			) : null}
		</div>
	);
}

// One of the two desktop rows: a labelled group whose start reads from the
// left and whose end sits at the right edge. Nothing is drawn for an empty row.
// The items page's two rows, pinned beneath the shell header from 768px up
// (KPR-1724); the pin publishes its height for the sticky layers below it.
function TwoRowToolbar({ view, query }) {
	const ref = useRef(null);
	useListToolbarPin(ref);
	return (
		<div ref={ref} className="items-page__toolbar items-page__toolbar--two-row">
			<ToolbarRow kind="view" label="What you are looking at" row={view} />
			<ToolbarRow kind="query" label="How the list is narrowed and ordered" row={query} />
		</div>
	);
}

function ToolbarRow({ kind, label, row }) {
	const start = row?.start || null;
	const end = row?.end || null;
	if (!hasContent(start) && !hasContent(end)) return null;
	return (
		<div className={`items-page__toolbar-row items-page__toolbar-row--${kind}`} role="group" aria-label={label} data-toolbar-row={kind}>
			<div className="items-page__toolbar-start">{start}</div>
			{hasContent(end) ? <div className="items-page__toolbar-end">{end}</div> : null}
		</div>
	);
}

// A fragment of nulls is no content: the page passes its controls as they
// are, and most are null where they do not apply.
function hasContent(node) {
	if (node == null || node === false) return false;
	if (Array.isArray(node)) return node.some(hasContent);
	if (React.isValidElement(node) && node.type === React.Fragment) return hasContent(React.Children.toArray(node.props.children));
	return true;
}
